// What the consent prompt says when "I agree & continue" could not be recorded.
//
// The prompt is full-screen and blocks the whole app, so its error line is the
// ONLY thing the person can act on. It used to print `e.message` raw — a
// `TypeError: Failed to fetch` on a dead link, a PostgREST code otherwise —
// at someone who could neither fix it nor leave. Now it is always a sentence,
// and the commonest cause, no connection, says so and points at Sign out.
//
// ⚠️ friendlyError deliberately passes UNMAPPED driver text (and our own P0001
// RAISE text) through verbatim — right for a capture form whose operator can
// quote it to support, wrong here. A result with no `detail` is that
// passthrough, so it becomes NOT_RECORDED. Main.tsx still reports the raw
// error from the mutation cache, so nothing is lost for diagnosis.

import { friendlyError } from '@/lib/friendlyError'
import { isNetworkFailure } from '@/lib/errorReporter'

export const NO_CONNECTION =
  'There’s no connection, so your acceptance wasn’t recorded. '
  + 'Try again when this device has signal — or tap Sign out to leave it for the next person.'

export const SIGN_IN_EXPIRED =
  'Your sign-in has expired, so your acceptance wasn’t recorded. Tap Sign out and sign in again.'

export const NOT_RECORDED =
  'Your acceptance wasn’t recorded. Try again — if it keeps failing, tap Sign out and tell your administrator.'

// record_user_consent's own RAISEs (baseline body; fn_source: live = baseline).
const CONSENT_DOMAIN: [RegExp, string][] = [
  [/must be called as an authenticated user/i, SIGN_IN_EXPIRED],
  [/JWT expired|invalid JWT|not authenticated/i, SIGN_IN_EXPIRED],
]

export function consentFailureText(err: unknown): string {
  // Before friendlyError: a timeout (withTimeout's "took too long") and a
  // failed fetch are both "no connection" to the person holding the device.
  if (isNetworkFailure(err)) return NO_CONNECTION
  const f = friendlyError(err, CONSENT_DOMAIN)
  return f.detail === null ? NOT_RECORDED : f.message
}
